import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ExportMenu } from "@/components/reports/ExportMenu";
import { PoStatusBadge } from "@/components/purchases/PoStatusBadge";
import { PO_STATUSES, PO_TYPES, money } from "@/lib/constants";
import { Search, Plus, Filter, ShoppingCart } from "lucide-react";

export default function Compras() {
  const { hasPermission } = useAuth();
  const { isSuperAdmin, activeTenantId, branches } = useTenant();
  const navigate = useNavigate();
  const canQuery = !isSuperAdmin || !!activeTenantId;
  const canCreate = hasPermission("purchase_orders:create");

  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterSupplier, setFilterSupplier] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};

  const params = {
    ...tenantParam,
    search: search || undefined,
    status: filterStatus !== "all" ? filterStatus : undefined,
    branch_id: filterBranch !== "all" ? filterBranch : undefined,
    supplier_id: filterSupplier !== "all" ? filterSupplier : undefined,
    po_type: filterType !== "all" ? filterType : undefined,
    date_from: filterDateFrom || undefined,
    date_to: filterDateTo || undefined,
  };

  async function fetchList() {
    if (!canQuery) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const [{ data }, { data: sup }] = await Promise.all([
        api.get("/purchase-orders", { params }),
        api.get("/suppliers", { params: { ...tenantParam, include_inactive: true } }),
      ]);
      setItems(data);
      setSuppliers(sup);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search, filterStatus, filterBranch, filterSupplier, filterType,
    filterDateFrom, filterDateTo, activeTenantId, isSuperAdmin,
  ]);

  const totalPeriod = items
    .filter((po) => !["cancelada", "rechazada"].includes(po.status))
    .reduce((acc, po) => acc + Number(po.total || 0), 0);

  return (
    <div className="space-y-6" data-testid="page-compras">
      <PageHeader
        title="Órdenes de compra"
        description="Registro formal de compras a proveedores. Cada OC pertenece a una sucursal y un proveedor."
        actions={
          <div className="flex items-center gap-2">
            <ExportMenu
              endpoint="/reports/purchases"
              params={{
                ...tenantParam,
                status: filterStatus !== "all" ? filterStatus : undefined,
                branch_id: filterBranch !== "all" ? filterBranch : undefined,
                supplier_id: filterSupplier !== "all" ? filterSupplier : undefined,
                po_type: filterType !== "all" ? filterType : undefined,
                date_from: filterDateFrom || undefined,
                date_to: filterDateTo || undefined,
              }}
              permission="purchase_orders:export"
              filename="ordenes-de-compra"
              testId="export-purchases"
            />
            {canCreate && (
              <Button
                className="btn-tenant"
                onClick={() => navigate("/compras/nueva")}
                disabled={!canQuery}
                data-testid="btn-new-po"
              >
                <Plus className="h-4 w-4 mr-2" /> Nueva OC
              </Button>
            )}
          </div>
        }
      />

      {/* Filters */}
      <div className="bg-white border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <Filter className="h-3.5 w-3.5" /> Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar folio…"
              className="pl-9 h-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="po-search"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="po-filter-status"><SelectValue placeholder="Estatus" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estatus</SelectItem>
              {PO_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterBranch} onValueChange={setFilterBranch}>
            <SelectTrigger data-testid="po-filter-branch"><SelectValue placeholder="Sucursal" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sucursal activa</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterSupplier} onValueChange={setFilterSupplier}>
            <SelectTrigger data-testid="po-filter-supplier"><SelectValue placeholder="Proveedor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los proveedores</SelectItem>
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.trade_name || s.legal_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger data-testid="po-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {PO_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2 lg:col-span-2">
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              data-testid="po-filter-date-from"
            />
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              data-testid="po-filter-date-to"
            />
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="text-xs text-muted-foreground" data-testid="po-count">
          {items.length} orden{items.length === 1 ? "" : "es"}
        </div>
        <div className="text-xs text-muted-foreground">
          Total período (excluye canceladas / rechazadas):{" "}
          <span className="font-medium text-slate-800 mono" data-testid="po-total-period">{money(totalPeriod)}</span>
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={canQuery ? "Sin órdenes de compra" : "Selecciona un tenant"}
            description={canQuery ? "Crea la primera OC para registrar una compra formal." : ""}
            icon={ShoppingCart}
            action={canCreate && canQuery && (
              <Button className="btn-tenant" onClick={() => navigate("/compras/nueva")}>
                <Plus className="h-4 w-4 mr-2" /> Nueva OC
              </Button>
            )}
          />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Folio</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>Sucursal</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Estatus</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((po) => (
                <TableRow
                  key={po.id}
                  className="tbl-row cursor-pointer"
                  onClick={() => navigate(`/compras/${po.id}`)}
                  data-testid={`row-po-${po.id}`}
                >
                  <TableCell className="mono font-medium text-tenant">{po.folio}</TableCell>
                  <TableCell className="text-sm">{po.issue_date}</TableCell>
                  <TableCell className="text-sm">{po.supplier_name || "—"}</TableCell>
                  <TableCell className="text-sm">{po.branch_name || "—"}</TableCell>
                  <TableCell className="text-xs uppercase text-muted-foreground">{po.po_type}</TableCell>
                  <TableCell className="text-right mono text-sm">{money(po.total)}</TableCell>
                  <TableCell>
                    <PoStatusBadge status={po.status} data-testid={`row-po-status-${po.id}`} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
